import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useBillingReconciliationDpeActions,
  useCreateBillingReconciliationDpeAction,
  useDeleteBillingReconciliationDpeAction,
  useUpdateBillingReconciliationDpeAction,
} from '../../hooks/api/useBillingReconciliationHooks.js';

const actionFingerprint = (actions = []) => actions.map((action) => [
  action?.id || '',
  action?.queueStatus || '',
  action?.productCd || action?.productCode || '',
  action?.updated_at || action?.updatedAt || '',
].join(':')).join('|');

export const useVarianceDrawerDpeActions = ({ row, runId, open, mockMode, enabled = true, prefetchedActions = null }) => {
  const [error, setError] = useState(null);
  const [localPrefetchedActions, setLocalPrefetchedActions] = useState([]);
  const prefetchedFingerprintRef = useRef('');
  const hasPrefetchedActions = Array.isArray(prefetchedActions);
  const query = useBillingReconciliationDpeActions(
    { runId, varianceId: row?.id, bac: row?.bac, limit: 200 },
    { enabled: enabled && open && !mockMode && Boolean(runId && row?.bac) && !hasPrefetchedActions },
  );
  const createMutation = useCreateBillingReconciliationDpeAction();
  const updateMutation = useUpdateBillingReconciliationDpeAction();
  const deleteMutation = useDeleteBillingReconciliationDpeAction();
  useEffect(() => {
    if (!open || mockMode || !hasPrefetchedActions) {
      prefetchedFingerprintRef.current = '';
      setLocalPrefetchedActions([]);
      return;
    }
    const nextFingerprint = actionFingerprint(prefetchedActions);
    if (nextFingerprint === prefetchedFingerprintRef.current) return;
    prefetchedFingerprintRef.current = nextFingerprint;
    setLocalPrefetchedActions(prefetchedActions);
  }, [hasPrefetchedActions, mockMode, open, prefetchedActions]);

  const actions = useMemo(() => (mockMode ? [] : (hasPrefetchedActions ? localPrefetchedActions : (query.data || []))), [
    hasPrefetchedActions,
    localPrefetchedActions,
    mockMode,
    query.data,
  ]);

  const persistDpeAction = useCallback(async (action) => {
    if (mockMode || !runId) return action;
    try {
      setError(null);
      const saved = action?.id
        ? await updateMutation.mutateAsync({ id: action.id, runId, patch: action })
        : await createMutation.mutateAsync({ runId, action });
      if (hasPrefetchedActions) {
        setLocalPrefetchedActions((current) => (
          current.some((existing) => existing.id === saved.id)
            ? current.map((existing) => (existing.id === saved.id ? saved : existing))
            : [saved, ...current]
        ));
      }
      return saved;
    } catch (nextError) {
      setError(nextError);
      throw nextError;
    }
  }, [createMutation, hasPrefetchedActions, mockMode, runId, updateMutation]);

  const removeDpeAction = useCallback(async (id) => {
    if (mockMode || !id) return null;
    try {
      setError(null);
      const deleted = await deleteMutation.mutateAsync({ id, runId });
      if (hasPrefetchedActions) {
        setLocalPrefetchedActions((current) => current.filter((action) => action.id !== id));
      }
      return deleted;
    } catch (nextError) {
      setError(nextError);
      throw nextError;
    }
  }, [deleteMutation, hasPrefetchedActions, mockMode, runId]);

  return {
    dpeActions: actions,
    dpeActionError: error,
    isDpeActionBusy: createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
    persistDpeAction,
    removeDpeAction,
    setDpeActionError: setError,
  };
};
